import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  contentChildren,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from 'primeng/dynamicdialog';

import { GrButtonComponent } from '@components/button';
import { GrModalFooterComponent, GrTooltipDirective, type GrModalResponse } from '@components/overlays';

import { GrCanvasComponent } from '../canvas';
import { GrCanvasRendererDirective } from '../canvas-renderer';
import type { GrCanvasItem, GrCanvasMoveEvent, GrCanvasResizeEvent } from '../canvas/canvas.types';
import type { CanvasEditorApi } from './canvas-editor-store.types';
import type { CanvasEditorAddModal, CanvasEditorConfigureModal } from './canvas-editor.types';

@UntilDestroy()
@Component({
  selector: 'gr-canvas-editor',
  imports: [GrCanvasComponent, GrButtonComponent, GrTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'gr-canvas-editor block w-full' },
  templateUrl: './canvas-editor.component.html',
})
export class GrCanvasEditorComponent {
  private readonly dialogs = inject(DialogService);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly store = input.required<CanvasEditorApi>();
  readonly addModal = input<CanvasEditorAddModal | null>(null);
  readonly configureModals = input<Record<string, CanvasEditorConfigureModal>>({});

  protected readonly renderers = contentChildren(GrCanvasRendererDirective, { descendants: true });

  readonly cols = input<number>(12);
  readonly rowHeight = input<string>('5rem');
  readonly phantom = input<GrCanvasItem | null>(null);
  readonly emptyLabel = input<string>('No widgets yet — add one to get started.');
  readonly emptyIcon = input<string>('dashboard_customize');
  readonly customizeTitle = input<string>('Customizing layout');
  readonly customizeSubtitle = input<string>(
    'Drag widgets to move them, resize from the edges, or remove them with the close icon.',
  );
  readonly showBuilder = input<boolean>(false);
  readonly builderClick = output<void>();
  readonly showTableBuilder = input<boolean>(false);
  readonly tableBuilderClick = output<void>();
  readonly showChartBuilder = input<boolean>(false);
  readonly chartBuilderClick = output<void>();
  readonly itemEditRequested = output<{ item: GrCanvasItem; x: number; y: number }>();
  readonly dragInterceptor = input<
    ((item: GrCanvasItem, event: PointerEvent) => boolean) | null
  >(null);
  readonly minSizeById = input<Record<string, { readonly colSpan: number; readonly rowSpan: number }>>({});

  protected readonly canvas = viewChild<GrCanvasComponent>(GrCanvasComponent);

  projectPoint(
    clientX: number,
    clientY: number,
    colSpan: number,
  ): { x: number; y: number; insideGrid: boolean } | null {
    return this.canvas()?.projectPoint(clientX, clientY, colSpan) ?? null;
  }

  maxDropY(rowSpan: number): number {
    return this.canvas()?.maxDropY(rowSpan) ?? 0;
  }

  getScrollContainer(): HTMLElement {
    let el: HTMLElement | null = this.hostRef.nativeElement.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
      el = el.parentElement;
    }

    return document.documentElement;
  }

  isPointBelow(clientY: number): boolean {
    const el = this.canvas()?.hostElement;
    if (!el) return false;
    return clientY > el.getBoundingClientRect().bottom;
  }

  isPointInside(clientX: number, clientY: number): boolean {
    const el = this.canvas()?.hostElement;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  }

  protected undo(): void {
    this.store().undo();
  }

  protected reset(): void {
    this.store().reset();
  }

  protected cancel(): void {
    this.store().cancel();
  }

  protected save(): void {
    this.store().save();
  }

  protected onMoved(event: GrCanvasMoveEvent): void {
    this.store().move(event.id, event.x, event.y);
  }

  protected onResized(event: GrCanvasResizeEvent): void {
    this.store().resize(event.id, event.colSpan, event.rowSpan);
  }

  protected onRemoveRequested(id: string): void {
    this.store().remove(id);
  }

  protected onDeleteRowsRequested(e: { startRow: number; count: number }): void {
    this.store().deleteRows(e.startRow, e.count);
  }

  protected onEditRequested(e: { id: string; x: number; y: number }): void {
    const item = this.store().findById(e.id);
    if (!item) return;
    this.itemEditRequested.emit({ item, x: e.x, y: e.y });
  }

  protected openAdd(): void {
    const modal = this.addModal();
    if (!modal) return;
    const ref = this.dialogs.open(modal.component, {
      header: modal.header,
      width: modal.width,
      breakpoints: modal.breakpoints,
      modal: true,
      closable: true,
      data: { buttons: modal.buttons },
      templates: { footer: GrModalFooterComponent },
    });
    if (!ref) return;
    ref.onClose.pipe(untilDestroyed(this)).subscribe((res?: GrModalResponse<unknown>) => {
      if (!res || res.cancelled) return;
      const kind = modal.resolveKind(res.form);
      if (!kind) return;
      this.openConfigure(kind, null);
    });
  }

  private openConfigure(kind: string, existing: GrCanvasItem | null): void {
    const modal = this.configureModals()[kind];
    if (!modal) return;
    const ref = this.dialogs.open(modal.component, {
      header: modal.header,
      width: modal.width,
      breakpoints: modal.breakpoints,
      modal: true,
      closable: true,
      data: { buttons: modal.buttons, payload: { initial: existing?.config, colSpan: existing?.colSpan, rowSpan: existing?.rowSpan } },
      templates: { footer: GrModalFooterComponent },
    });
    if (!ref) return;
    ref.onClose.pipe(untilDestroyed(this)).subscribe((res?: GrModalResponse<unknown>) => {
      if (!res || res.cancelled) {
        if (!existing) this.openAdd();
        return;
      }
      if (res.form === undefined) return;
      if (existing) {
        this.store().update(existing.id, res.form);
      } else {
        this.store().add(kind, res.form);
      }
    });
  }
}
